import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import BackToAdminLink from "@/components/BackToAdminLink";
import { Article, Plus, ArrowClockwise, Trash, PencilSimple } from "@phosphor-icons/react";

const TYPES = [
  { value: "blog", label: "Blog" },
  { value: "help", label: "Help" },
  { value: "changelog", label: "Changelog" },
];

const STATUS_BADGE = {
  draft: "bg-black/10 text-black/70",
  scheduled: "bg-[#FFBD4A]",
  published: "bg-emerald-100 border-emerald-700 text-emerald-900",
};

export default function AdminArticlesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (typeFilter) q.set("type", typeFilter);
      if (statusFilter) q.set("status", statusFilter);
      const res = await axios.get(`${API}/admin/articles?${q.toString()}`);
      setItems(res.data.articles || []);
    } catch (e) {
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter]);

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This unpublishes it from the marketing site too.`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (user && !user.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="admin-articles-page">
      <div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <BackToAdminLink />
            <div className="section-eyebrow flex items-center gap-2">
              <Article size={16} weight="bold" /> Admin · CMS
            </div>
            <h1 className="font-heading uppercase text-4xl mt-2">Articles</h1>
          </div>
          <div className="flex gap-2 shrink-0 mt-1">
            <Link
              to="/admin/articles/new"
              className="btn-primary"
              data-testid="admin-article-new"
            >
              <Plus size={14} weight="bold" /> New article
            </Link>
            <button
              type="button"
              className="btn-secondary"
              onClick={load}
              disabled={loading}
              data-testid="admin-articles-reload"
            >
              <ArrowClockwise size={14} weight="bold" /> {loading ? "…" : "Refresh"}
            </button>
          </div>
        </div>
        <p className="text-black/75 mt-3 leading-relaxed max-w-3xl">
          Draft, schedule, and publish articles for the blog, help centre, and
          changelog. Every published article commits directly to the marketing
          GitHub repo (Cloudflare Pages auto-rebuilds), so what you write here
          shows up on <span className="font-semibold">forecastly.me</span> a
          minute or so later.
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap" data-testid="admin-articles-filters">
        <select
          className="input !w-auto"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="filter-type"
        >
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          className="input !w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="filter-status"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="published">Published</option>
        </select>
      </div>

      {/* Table */}
      <div className="card !p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF7E5]">
            <tr className="text-left text-xs uppercase font-heading tracking-widest">
              <th className="p-3">Title</th>
              <th className="p-3">Type</th>
              <th className="p-3">Status</th>
              <th className="p-3">Publish</th>
              <th className="p-3 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-black/50">
                  No articles yet. Click <span className="font-semibold">New article</span> to write one.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-[#FFF7E5]/50" data-testid={`article-row-${item.id}`}>
                  <td className="p-3">
                    <Link to={`/admin/articles/${item.id}`} className="font-semibold hover:underline">
                      {item.title}
                    </Link>
                    {item.excerpt && (
                      <div className="text-xs text-black/50 mt-1 line-clamp-1">{item.excerpt}</div>
                    )}
                  </td>
                  <td className="p-3 text-xs uppercase tracking-widest">{item.type}</td>
                  <td className="p-3">
                    <span
                      className={`text-[10px] uppercase tracking-widest font-heading px-2 py-1 rounded border-[1.5px] border-black ${STATUS_BADGE[item.status] || ""}`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-black/70">
                    {item.publish_at
                      ? new Date(item.publish_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                      : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-2">
                      <Link
                        to={`/admin/articles/${item.id}`}
                        className="btn-secondary !px-2 !py-1 text-xs"
                        title="Edit"
                      >
                        <PencilSimple size={12} weight="bold" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => remove(item)}
                        className="btn-secondary !px-2 !py-1 text-xs !border-red-700 !text-red-700"
                        title="Delete"
                        data-testid={`article-delete-${item.id}`}
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
